import React, { useState, useCallback, useEffect } from 'react'
import {
  Page,
  Layout,
  Card,
  Box,
  InlineGrid,
  InlineStack,
  Text,
  Badge,
  Button,
  ProgressBar,
  Divider,
  IndexTable,
  IndexFilters,
  useSetIndexFiltersMode,
  ChoiceList,
  Pagination,
  EmptyState,
  Spinner,
} from '@shopify/polaris'
import { AutomationIcon } from '@shopify/polaris-icons'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDistance } from 'date-fns'

import { getJobs, getLatestJobChain } from '../../../apis/jobs'
import { JobStatus, JobStatusConfig, needsRefresh } from '../../../constants/jobStatus'

export default function JobsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedTab, setSelectedTab] = useState(0)
  const [queryValue, setQueryValue] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [refreshInterval, setRefreshInterval] = useState(null)
  const { mode, setMode } = useSetIndexFiltersMode()

  const statusTabs = [
    { id: 'all', content: t('Jobs.status.all'), value: JobStatus.ALL },
    { id: 'pending', content: t('Jobs.status.pending'), value: JobStatus.PENDING },
    { id: 'started', content: t('Jobs.status.started'), value: JobStatus.STARTED },
    { id: 'running', content: t('Jobs.status.running'), value: JobStatus.RUNNING },
    { id: 'completed', content: t('Jobs.status.completed'), value: JobStatus.COMPLETED },
    { id: 'failed', content: t('Jobs.status.failed'), value: JobStatus.FAILED },
  ]

  const currentStatus = statusTabs[selectedTab].value

  // Format job type for display
  const formatJobType = (type) => {
    const translationKey = `Jobs.types.${type}`
    const translated = t(translationKey)
    // If translation exists, use it; otherwise format the type
    if (translated !== translationKey) {
      return translated
    }
    // Fallback: format the type name
    return type
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase())
  }

  const {
    data,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['jobs', currentStatus, currentPage, itemsPerPage, queryValue],
    queryFn: () => getJobs({ 
      status: currentStatus,
      page: currentPage,
      limit: itemsPerPage,
      search: queryValue
    }),
    keepPreviousData: true,
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: true,
    staleTime: refreshInterval ? 1000 : 5 * 60 * 1000,
  })

  // Get latest job chain for display
  const { data: chainData } = useQuery({
    queryKey: ['latestChain'],
    queryFn: () => getLatestJobChain({ type: 'shop_install' }),
    refetchInterval: refreshInterval,
    staleTime: refreshInterval ? 1000 : 5 * 60 * 1000,
  })

  // Check if any jobs are in pending or running state
  const hasActiveJobs = data?.jobs?.some(job => 
    job.status === JobStatus.PENDING || 
    job.status === JobStatus.RUNNING || 
    job.status === JobStatus.STARTED
  ) || chainData?.chain?.status === JobStatus.RUNNING

  // Handle page visibility changes and auto-refresh
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && hasActiveJobs) {
        // Refetch data when page becomes visible and there are active jobs
        refetch()
        // Also refetch chain data if it's running
        if (chainData?.chain?.status === JobStatus.RUNNING || 
            chainData?.chain?.status === JobStatus.PENDING ||
            chainData?.chain?.status === JobStatus.STARTED) {
          // Force refetch of chain data
          queryClient.invalidateQueries(['latestChain'])
        }
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    // Refetch on component mount if there are active jobs
    if (hasActiveJobs) {
      refetch()
    }
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [hasActiveJobs, refetch])

  // Auto-refresh based on active jobs
  useEffect(() => {
    if (hasActiveJobs) {
      setRefreshInterval(15000) // Enable refresh when jobs are active
    } else {
      setRefreshInterval(null) // Disable refresh when all jobs are complete
    }
    
    // Cleanup interval when component unmounts
    return () => {
      setRefreshInterval(null)
    }
  }, [hasActiveJobs])

  const getStatusBadge = (status) => {
    const config = JobStatusConfig[status]
    if (config) {
      return <Badge tone={config.tone}>{t(config.translationKey)}</Badge>
    }
    return <Badge tone="info">{status}</Badge>
  }

  const handleTabChange = useCallback((selected) => {
    setSelectedTab(selected)
    setCurrentPage(1)
  }, [])

  const handleQueryValueRemove = useCallback(() => setQueryValue(''), [])
  const handleQueryValueChange = useCallback((value) => setQueryValue(value), [])

  const handleClearAll = useCallback(() => {
    handleQueryValueRemove()
  }, [handleQueryValueRemove])

  const resourceName = {
    singular: 'job',
    plural: 'jobs',
  }

  // Helper function - define before using
  const isEmpty = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0
    } else {
      return value === '' || value == null
    }
  }

  const rowMarkup = data?.jobs?.map((job) => (
    <IndexTable.Row
      id={job.id}
      key={job.id}
      position={job.id}
      onClick={() => navigate(`/settings/jobs/${job.id}`)}
    >
      <IndexTable.Cell>
        <Box>
          <Text variant="bodyMd" fontWeight="bold">
            {job.name}
          </Text>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box>
          <Text variant="bodyMd" tone="subdued">
            {formatJobType(job.type)}
          </Text>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box>
          {getStatusBadge(job.status)}
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box>
          {job.progress !== undefined && job.progress !== null ? (
            <Box maxWidth="100px">
              <ProgressBar progress={job.progress} size="small" />
              <Box paddingBlockStart="100">
                <Text variant="bodySm" tone="subdued">
                  {job.progress}%
                </Text>
              </Box>
            </Box>
          ) : (
            <Text variant="bodySm" tone="subdued">-</Text>
          )}
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box>
          <Text variant="bodySm" tone="subdued">
            {job.retryCount > 0 ? `${job.retryCount}/${job.maxRetries}` : '-'}
          </Text>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box>
          <Text variant="bodySm" tone="subdued">
            {job.createdAt ? formatDistance(new Date(job.createdAt), new Date(), { addSuffix: true }) : '-'}
          </Text>
        </Box>
      </IndexTable.Cell>
    </IndexTable.Row>
  ))

  const appliedFilters = []
  if (!isEmpty(queryValue)) {
    appliedFilters.push({
      key: 'query',
      label: `Search: ${queryValue}`,
      onRemove: handleQueryValueRemove,
    })
  }

  const emptyStateMarkup = (
    <EmptyState
      heading={t('Jobs.emptyState.heading')}
      action={{ 
        content: t('Jobs.emptyState.action'),
        onAction: refetch 
      }}
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>{t('Jobs.emptyState.description')}</p>
    </EmptyState>
  )

  // Show installation chain status only if it's running or pending
  const installationBanner = chainData?.chain && 
    (chainData.chain.status === JobStatus.RUNNING || 
     chainData.chain.status === JobStatus.PENDING ||
     chainData.chain.status === JobStatus.STARTED) && (
    <Box paddingBlockEnd="400">
      <Card>
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="400" blockAlign="start">
            <Box paddingBlockStart="200">
              <AutomationIcon />
            </Box>
            <Box>
              <Text variant="headingMd">{t('Jobs.installationChain.title')}</Text>
              <Text variant="bodySm" tone="subdued">
                {t('Jobs.installationChain.description')}
              </Text>
            </Box>
          </InlineStack>
          <Box>
            {getStatusBadge(chainData.chain.status)}
          </Box>
        </InlineStack>
        {chainData.jobs && chainData.jobs.length > 0 && (
          <>
            <Box paddingBlockStart="400">
              <Divider />
            </Box>
            <Box paddingBlockStart="400">
              {(() => {
                // Jobs are already filtered by API (hidden jobs excluded)
                const visibleCompleted = chainData.jobs.filter(j => j.status === JobStatus.COMPLETED).length
                const visibleTotal = chainData.jobs.length
                const progressPercent = visibleTotal > 0 ? (visibleCompleted / visibleTotal) * 100 : 0
                
                return (
                  <>
                    <ProgressBar 
                      progress={progressPercent} 
                      size="small" 
                    />
                    <Box paddingBlockStart="200">
                      <Text variant="bodySm" tone="subdued">
                        {t('Jobs.installationChain.progress', {
                          completed: visibleCompleted,
                          total: visibleTotal
                        })}
                      </Text>
                    </Box>
                  </>
                )
              })()}
            </Box>
          </>
        )}
      </Card>
    </Box>
  )

  return (
    <Page
      title={
        hasActiveJobs ? (
          <InlineStack gap="200">
            <Text as="span" variant="headingLg">{t('Jobs.title')}</Text>
            <Badge tone="warning">{t('Jobs.autoUpdating')}</Badge>
          </InlineStack>
        ) : t('Jobs.title')
      }
      subtitle={t('Jobs.subtitle')}
      compactTitle
    >
      <Layout>
        {installationBanner && (
          <Layout.Section>
            {installationBanner}
          </Layout.Section>
        )}
        <Layout.Section>
          <Card padding="0">
        <IndexFilters
          queryValue={queryValue}
          queryPlaceholder={t('Jobs.searchPlaceholder')}
          onQueryChange={handleQueryValueChange}
          onQueryClear={handleQueryValueRemove}
          tabs={statusTabs}
          selected={selectedTab}
          onSelect={handleTabChange}
          canCreateNewView={false}
          appliedFilters={appliedFilters}
          onClearAll={handleClearAll}
          mode={mode}
          setMode={setMode}
          loading={isFetching}
        />
        {isLoading ? (
          <Box padding="600">
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Box>
        ) : data?.jobs?.length > 0 ? (
          <>
            <IndexTable
              resourceName={resourceName}
              itemCount={data.jobs.length}
              selectable={false}
              headings={[
                { title: t('Jobs.table.name') },
                { title: t('Jobs.table.type') },
                { title: t('Jobs.table.status') },
                { title: t('Jobs.table.progress') },
                { title: t('Jobs.table.retries') },
                { title: t('Jobs.table.created') },
              ]}
            >
              {rowMarkup}
            </IndexTable>
            <Box padding="400">
              <InlineStack align="center">
                <Pagination
                  hasPrevious={currentPage > 1}
                  hasNext={currentPage < data.totalPages}
                  onPrevious={() => setCurrentPage(currentPage - 1)}
                  onNext={() => setCurrentPage(currentPage + 1)}
                  label={`${data.total} ${data.total === 1 ? 'job' : 'jobs'}`}
                />
              </InlineStack>
            </Box>
          </>
        ) : (
          emptyStateMarkup
        )}
      </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}